import React from "react";
import PropTypes from "prop-types";
import { last, head } from "lodash/fp";
import TagsInput from "./TagsInput";
import * as Yup from "yup";
import * as palette from "@govlaunch/palette";

const checkIsValidEmail = email =>
  Yup.string()
    .email()
    .required()
    .isValidSync(email);

/**
 * @param  {String} error
 * @param  {Boolean} valid
 * @return {?String}
 */
const getValidationState = ({ touched, error, valid }) => {
  if (!touched) {
    return null;
  }

  if (error) {
    return "error";
  }

  if (valid) {
    return "success";
  }

  return null;
};

export default function EmailTagsInputField({ input, meta, placeholder, ...rest }) {
  const { touched, error } = meta;
  const hasAnyEmail = Array.isArray(input.value) && input.value.length > 0;

  return (
    <>
      <TagsInput
        value={input.value || []}
        onChange={(emails, event) => {
          const lastEmail = last(emails);
          const isValidEmail = checkIsValidEmail(lastEmail);

          // It should always forward the change event when removing an email
          if (event.key === "Backspace") {
            return input.onChange(emails);
          }

          if (!isValidEmail) {
            return;
          }

          input.onChange(emails);
        }}
        onFocus={input.onFocus}
        onBlur={(emails, e) => {
          if (!e.target.value) {
            input.onBlur(emails);

            return;
          }

          const email = e.target.value;
          const isValidEmail = checkIsValidEmail(email);

          if (isValidEmail) {
            input.onBlur([...emails, email]);
          }
        }}
        validationState={getValidationState(meta)}
        placeholder={!hasAnyEmail ? placeholder : null}
        {...rest}
      />
      {touched && error && <Error>{Array.isArray(error) ? head(error) : error}</Error>}
    </>
  );
}

EmailTagsInputField.propTypes = {
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  placeholder: PropTypes.string,
};

EmailTagsInputField.defaultProps = {
  placeholder: "Type e-mails...",
};

function Error({ children, ...props }) {
  return (
    <div
      css={{
        color: palette.red,
        fontSize: 12,
        fontWeight: 300,
        marginTop: 7,
      }}
      {...props}
    >
      {children}
    </div>
  );
}

Error.propTypes = {
  children: PropTypes.node.isRequired,
};
